import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

/**
 * Enregistre le service worker au mount et affiche un mini-prompt
 * d'installation quand le navigateur émet `beforeinstallprompt`.
 * Le prompt n'apparaît qu'après 30s d'usage et peut être ignoré 7 jours.
 */
export const PWAInstaller = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Enregistre le service worker (uniquement en prod-like, pas dans iframe preview)
    if ("serviceWorker" in navigator && window.self === window.top) {
      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/sw.js")
          .then((reg) => {
            // Force la vérification d'une nouvelle version au load.
            reg.update().catch(() => {});

            // Si un nouveau SW est en attente → on lui dit de skipWaiting,
            // puis on recharge la page une seule fois pour servir la nouvelle UI.
            const triggerUpdate = (worker) => {
              if (!worker) return;
              worker.addEventListener("statechange", () => {
                if (worker.state === "installed" && navigator.serviceWorker.controller) {
                  worker.postMessage({ type: "SKIP_WAITING" });
                }
              });
            };
            triggerUpdate(reg.installing);
            reg.addEventListener("updatefound", () => triggerUpdate(reg.installing));

            let reloaded = false;
            navigator.serviceWorker.addEventListener("controllerchange", () => {
              if (reloaded) return;
              reloaded = true;
              window.location.reload();
            });
          })
          .catch(() => { /* silent */ });
      });
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Vérifie qu'on n'a pas déjà refusé récemment
      const dismissed = parseInt(localStorage.getItem("pwa-install-dismissed-at") || "0", 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (dismissed && Date.now() - dismissed < sevenDays) return;

      // Affiche après 30s
      setTimeout(() => setShowPrompt(true), 30000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-install-dismissed-at", Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm bg-card border-2 border-primary rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-bottom-2"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        aria-label="Fermer"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Download className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-medium text-sm">Installer Bati Assistant</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Accès rapide depuis votre écran d'accueil, sans navigateur.
          </p>
          <Button
            size="sm"
            onClick={handleInstall}
            className="mt-2 w-full sm:w-auto"
            data-testid="pwa-install-confirm"
          >
            Installer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstaller;
